import { FC, useState, useEffect } from "react";
import api from "../services/api";
import { getCategoryBadge, getStatusBadge } from "../utils/utils";
import type { QrCode, Page, CategoryType, StatusType, ApiResponse } from "../types";
import { useData } from "../context/DataContext";
import QrCodeDisplayModal from "../components/QrCodeDisplayModal"; // 1. Importa il componente della modale

const ClientQrCodesPage: FC = () => {
    const [qrCodePage, setQrCodePage] = useState<Page<QrCode> | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(0);

    const [activeCategory, setActiveCategory] = useState<number>(-1);
    const { categories } = useData();

    const [showQrModal, setShowQrModal] = useState<QrCode | null>(null);

    useEffect(() => {
        const fetchClientQrCodes = async (page: number, categoryId: number) => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await api.get<ApiResponse<Page<QrCode>>>(`/api/qrcodes/getAll`, {
                    params: {
                        page: page,
                        size: 15,
                        idCategory: categoryId,
                    },
                });

                if (response.data.status) {
                    setQrCodePage(response.data.data);
                } else {
                    throw new Error(response.data.message || "Impossibile caricare i QR code.");
                }
            } catch (err: any) {
                setError(err.message || "Si è verificato un errore di rete.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchClientQrCodes(currentPage, activeCategory);
    }, [currentPage, activeCategory]);

    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setCurrentPage(0);
        setActiveCategory(Number(e.target.value));
    };

    return (
        <div>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2">I Miei QR Code</h1>
                    <p className="text-slate-400">Elenco di tutti i QR code a te associati.</p>
                </div>
                <select onChange={handleCategoryChange} value={activeCategory} className="bg-slate-700/50 border border-slate-600 px-4 py-2 rounded-lg text-slate-300 hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 self-start md:self-center">
                    <option value={-1}>Tutte le categorie</option>
                    {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="text-xs text-slate-400 uppercase bg-slate-800">
                        <tr>
                            <th className="p-4">Nome / Descrizione</th>
                            <th className="p-4">Categoria</th>
                            <th className="p-4 hidden sm:table-cell">Scadenza</th>
                            <th className="p-4">Stato</th>
                            {/* 3. Aggiungi l'intestazione per la colonna Azioni */}
                            <th className="p-4 text-right">Azioni</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700">
                        {isLoading ? (
                            <tr><td colSpan={5} className="text-center p-8 text-slate-400">Caricamento...</td></tr>
                        ) : error ? (
                            <tr><td colSpan={5} className="text-center p-8 text-red-400">{error}</td></tr>
                        ) : !qrCodePage || qrCodePage.content.length === 0 ? (
                            <tr><td colSpan={5} className="text-center p-8 text-slate-400">Nessun QR code trovato.</td></tr>
                        ) : (
                            qrCodePage.content.map(qr => (
                                <tr key={qr.id} className="hover:bg-slate-800 transition-colors">
                                    <td className="p-4 text-white font-medium">{qr.name}</td>
                                    <td className="p-4">{getCategoryBadge(qr.category as CategoryType)}</td>
                                    <td className="p-4 text-slate-300 hidden sm:table-cell">{qr.validTo || 'Nessuna'}</td>
                                    <td className="p-4">{getStatusBadge(qr.status as StatusType)}</td>
                                    {/* 4. Aggiungi la cella con il pulsante Visualizza */}
                                    <td className="p-4 text-right">
                                        <button onClick={() => setShowQrModal(qr)} className="text-sky-400 hover:text-sky-300 text-sm font-semibold">
                                            Visualizza
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
                {qrCodePage && qrCodePage.totalPages > 1 && (
                    <div className="p-4 border-t border-slate-700 flex justify-between items-center text-sm text-slate-400">
                        <button onClick={() => setCurrentPage(p => p - 1)} disabled={qrCodePage.number === 0 || isLoading} className="px-3 py-1 rounded-md bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed">Precedente</button>
                        <span>Pagina {qrCodePage.number + 1} di {qrCodePage.totalPages}</span>
                        <button onClick={() => setCurrentPage(p => p + 1)} disabled={qrCodePage.number >= qrCodePage.totalPages - 1 || isLoading} className="px-3 py-1 rounded-md bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed">Successiva</button>
                    </div>
                )}
            </div>

            {/* 5. Renderizza la modale quando lo stato è attivo */}
            {showQrModal && (
                <QrCodeDisplayModal
                    qrCode={showQrModal}
                    onClose={() => setShowQrModal(null)}
                />
            )}
        </div>
    );
};

export default ClientQrCodesPage;
